import axios from 'axios';
import { setTokens, clearTokens } from './authToken';
import { isValidAuthToken } from '../utils/authSession';
import { forceUserLogout, isAccountDeactivatedError, isAccountDeletedError, DELETED_MSG } from '../utils/accountGuard';

export const BASE_URL = process.env.REACT_APP_BASE_URL;
export const API_URL  = `${BASE_URL}/api`;

const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/login-otp',
  '/auth/signup',
  '/auth/send-otp',
  '/auth/verify-otp',
  '/auth/reset-password',
];

const isPublicAuthRequest = (url = '') =>
  PUBLIC_AUTH_PATHS.some((path) => url.includes(path));

const getRequestRole = (url = '') => {
  if (url.includes('/presence/') || url.includes('/visitors/')) return 'user';
  if (window.location.pathname.startsWith('/partner')) return 'partner';
  if (window.location.pathname.startsWith('/admin')) return 'admin';
  return 'user';
};

const getAccessTokenForRole = (role) => {
  if (role === 'partner') return localStorage.getItem('partner_access_token');
  if (role === 'admin') return localStorage.getItem('admin_access_token');
  return localStorage.getItem('user_access_token');
};

const getRefreshTokenForRole = (role) => {
  if (role === 'partner') return localStorage.getItem('partner_refresh_token');
  if (role === 'admin') return localStorage.getItem('admin_refresh_token');
  return localStorage.getItem('user_refresh_token');
};

const refreshStateByRole = {
  user: { isRefreshing: false, failedQueue: [] },
  admin: { isRefreshing: false, failedQueue: [] },
  partner: { isRefreshing: false, failedQueue: [] },
};

const processQueue = (role, error, token = null) => {
  refreshStateByRole[role].failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  refreshStateByRole[role].failedQueue = [];
};

const API = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

API.interceptors.request.use(config => {
  if (isPublicAuthRequest(config.url)) return config;

  const role = getRequestRole(config.url);
  const token = getAccessTokenForRole(role);
  if (isValidAuthToken(token)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config._authRole = role;
  return config;
}, err => Promise.reject(err));

API.interceptors.response.use(
  res => res,
  async err => {
    const originalRequest = err.config;

    // Do not attempt refresh if the endpoint was the refresh endpoint itself
    if (originalRequest.url.includes('/refresh')) {
      return Promise.reject(err);
    }

    if (err.response?.status === 401 && !originalRequest._retry) {
      if (isPublicAuthRequest(originalRequest.url)) {
        return Promise.reject(err);
      }

      const role = originalRequest._authRole || getRequestRole(originalRequest.url);
      const state = refreshStateByRole[role];

      if (state.isRefreshing) {
        return new Promise(function(resolve, reject) {
          state.failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return API(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      state.isRefreshing = true;

      const refreshToken = getRefreshTokenForRole(role);
      const refreshUrl = role === 'user' ? '/auth/refresh' : '/admin/refresh';

      if (!isValidAuthToken(refreshToken)) {
        state.isRefreshing = false;
        return Promise.reject(err);
      }

      try {
        const { data } = await axios.post(`${API_URL}${refreshUrl}`, { refresh_token: refreshToken });
        const newAccess = data.token || data.access_token;
        const newRefresh = data.refresh_token || data.refersh_token;
        
        setTokens(role, newAccess, newRefresh);
        processQueue(role, null, newAccess);
        
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccess;
        return API(originalRequest);
      } catch (refreshErr) {
        processQueue(role, refreshErr, null);
        clearTokens(role);
        
        if (role === 'admin') window.location.href = '/admin/login';
        else if (role === 'partner') window.location.href = '/partner/login';
        else window.location.href = '/login';
        
        return Promise.reject(refreshErr);
      } finally {
        state.isRefreshing = false;
      }
    }

    if (err.response?.status === 403 && isAccountDeletedError(err)) {
      forceUserLogout(err.response?.data?.error || DELETED_MSG);
      return Promise.reject(err);
    }

    if (err.response?.status === 403 && isAccountDeactivatedError(err)) {
      forceUserLogout(err.response?.data?.error);
      return Promise.reject(err);
    }

    console.log('API Error:', err.response?.status, err.message);
    return Promise.reject(err);
  }
);

export default API;